import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import type { ActiveHotspot, OnboardingFlow, OnboardingStore } from '../types';
import { resolveActiveHotspot, resolveActiveInfoHotspots, applyTargetCompletion } from '../utils';

const useOnboardingStore = createStoreWithSubscribe<OnboardingStore>(
  (set, get) => ({
    flows: [],
    mountedTargets: [],
    completedHotspots: new Set<string>(),
    pendingTargets: new Set<string>(),
    activeHotspot: null,
    activeInfoHotspots: [],
    isGuideOpen: false,

    registerFlow: (flow: OnboardingFlow, initialCompletedHotspots?: string[]) => {
      set(
        (state) => {
          const exists = state.flows.some((f) => f.id === flow.id);
          if (exists) return;

          initialCompletedHotspots?.forEach((hotspot) => state.completedHotspots.add(hotspot));
          state.flows.push(flow);

          // Replay any queued target completions against the newly registered flow
          for (const target of state.pendingTargets) {
            if (applyTargetCompletion(flow, target, state.completedHotspots)) {
              state.pendingTargets.delete(target);
            }
          }
        },
        false,
        { type: 'REGISTER_FLOW' }
      );
      recompute(get, set);
    },

    unregisterFlow: (id: string) => {
      set(
        (state) => {
          state.flows = state.flows.filter((f) => f.id !== id);
        },
        false,
        { type: 'UNREGISTER_FLOW' }
      );
      recompute(get, set);
    },

    mountTarget: (target: string, instanceId: string) => {
      set(
        (state) => {
          state.mountedTargets = state.mountedTargets.filter((mounted) => mounted.instanceId !== instanceId);
          state.mountedTargets.push({ target, instanceId });
        },
        false,
        { type: 'MOUNT_TARGET' }
      );
      recompute(get, set);
    },

    unmountTarget: (instanceId: string) => {
      set(
        (state) => {
          state.mountedTargets = state.mountedTargets.filter((mounted) => mounted.instanceId !== instanceId);
        },
        false,
        { type: 'UNMOUNT_TARGET' }
      );
      recompute(get, set);
    },

    completeHotspot: (key: string) => {
      set(
        (state) => {
          state.completedHotspots.add(key);

          for (const flow of state.flows) {
            const completed = flow.hotspots.find((hotspot) => hotspot.key === key);
            if (!completed?.completes) continue;
            for (const additional of completed.completes) {
              state.completedHotspots.add(additional);
            }
          }
        },
        false,
        { type: 'COMPLETE_HOTSPOT' }
      );
      recompute(get, set);
    },

    completeHotspotByTarget: (target: string) => {
      const { flows } = get();
      const hasMatch = flows.some((flow) => flow.hotspots.some((hotspot) => hotspot.target === target));
      if (!hasMatch) {
        set(
          (state) => {
            state.pendingTargets.add(target);
          },
          false,
          { type: 'QUEUE_PENDING_TARGET' }
        );
        return;
      }
      set(
        (state) => {
          state.flows.forEach((flow) => applyTargetCompletion(flow, target, state.completedHotspots));
        },
        false,
        { type: 'COMPLETE_HOTSPOT_BY_TARGET' }
      );
      recompute(get, set);
    },

    toggleOnboardingGuide: () => {
      set(
        (state) => {
          state.isGuideOpen = !state.isGuideOpen;
        },
        false,
        { type: 'TOGGLE_ONBOARDING_GUIDE' }
      );
    },

    getFlowProgress: (flowId: string) => {
      const { flows, completedHotspots } = get();
      const flow = flows.find((f) => f.id === flowId);
      if (!flow) return [];
      return flow.hotspots.filter((hotspot) => completedHotspots.has(hotspot.key)).map((hotspot) => hotspot.key);
    },
  }),
  { name: 'OnboardingStore', trace: true }
);

export const useOnboardingShallowStore = createShallowStore(useOnboardingStore);

function recompute(
  get: () => OnboardingStore,
  set: (
    partial: Partial<OnboardingStore> | ((state: OnboardingStore) => Partial<OnboardingStore> | void),
    replace?: boolean,
    action?: { type: string }
  ) => void
) {
  const { flows, mountedTargets, completedHotspots } = get();
  const activeHotspot: ActiveHotspot | null = resolveActiveHotspot(flows, mountedTargets, completedHotspots);
  const activeInfoHotspots: ActiveHotspot[] = resolveActiveInfoHotspots(flows, mountedTargets, completedHotspots);
  set({ activeHotspot, activeInfoHotspots }, false, { type: 'RECOMPUTE_ACTIVE_HOTSPOT' });
}
